import {
  Avatar,
  Box,
  Divider,
  IconButton,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { fontSizes } from "../../theme/font-sizes";
import RaceInfo from "../utils/race/race-info";
import userDetails from "../utils/race/bet-details";
import { darkColors } from "../../theme/colors";
import UserDetails from "../utils/race/user-details";
import BetDetails from "../utils/race/bet-details";
import RacerProfile from "../utils/race/racer-profile";
import Wager from "../utils/race/wager";
import GradientButton from "../utils/gradient-button";
import englishTranslations from "../../i18n/translations/en";
import RaceGame from "../utils/race/race-game";
import BetTable from "../utils/race/bet-table";
import ConfirmationModal from "../utils/popups/confirmation-popup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { assetIcons, assetImages } from "../../constants/assets";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import RacerOdds from "../utils/race/racer-odds";
import BetPanel from "../utils/race/bet-panel";
import SwipeableDrawerComponent from "../utils/race/swipeable-drawer";
import routes from "../../constants/routes";
import { formatStreamDuration } from "../utils/format/time";
import apiRoutes from "../../api/routes";
import useApiRequest from "../../api/api";
import LoadingBackdrop from "../utils/loader";
import RacePageSkeleton from "./race-page-skeleton";
import { useUser } from "../../context/user-context";
import { BetsProvider, useBets } from "../../context/bets-context";
import { useSignalR } from "../../context/signalR-context";
import InlineSvg from "../utils/inline-svg";

export default function Race() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, updateUser } = useUser();
  const { isBettingClosed } = useSignalR(); // Get live flag from context
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const { makeApiCall } = useApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [raceData, setRaceData] = useState([]);
  const [userBets, setUserBets] = useState([]);
  const [wager, setWager] = useState("");
  const [racerId, setRacerId] = useState("");
  const [racerOdds, setRacerOdds] = useState([]);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { fetchBets, bets } = useBets();
  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const fetchRaces = async () => {
    setIsLoading(true);

    try {
      const response = await makeApiCall(apiRoutes.races.get(id), "GET");
      if (response?.data) {
        const race = response.data; // Response is a single object
        const mappedRace = {
          id: race.identifier, // Map the 'identifier' as 'id'
          raceName: race.name, // Map the 'name' as 'title'
          startTime: race.startTime, // Store startTime for future updates
          runnersInfo: `${race.racers.length} `, // Calculate the number of runners
          playbackUrl: race.playbackUrl,
          racers: race.racers,
          bettingWindowClosedOn: race.bettingWindowClosedOn,
        };
        document.title = race.name;

        // Set the mapped race data to the state
        setRaceData(mappedRace); // Wrap in an array if needed
      }
    } catch (error) {
      console.error("Failed to fetch races:", error);
    } finally {
    }
  };
  const fetchUserBets = async (showLoader = true) => {
    if (showLoader) setIsLoading(true);

    try {
      const response = await makeApiCall(apiRoutes.bets.getUserBets(id), "GET");
      if (response?.data) {
        const bets = response.data.map((bet) => ({
          racer: bet.betRacer, // Mapping username as racer
          amount: bet.betAmount, // Assigning the bet amount
        }));
        setUserBets(bets);
      }
    } catch (error) {
      console.error("Failed to fetch user bets:", error);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };
  const fetchRacerOdds = async (showLoader = true) => {
    if (showLoader) setIsLoading(true);

    try {
      const response = await makeApiCall(apiRoutes.races.racerOdds(id), "GET");
      console.log("racer odds", response.data);
      if (response?.data) {
        const racerOdds = response.data.map((data) => ({
          identifier: data.identifier,
          username: data.racerName,
          bettors: data.totalBettors,
          odds: data.racerOddFormatted,
          avatar: data.racerPicture,
        }));
        setRacerOdds(racerOdds);
      }
    } catch (error) {
      console.error("Failed to fetch user bets:", error);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  const updateRaceTimes = () => {
    setRaceData((prevRace) => ({
      ...prevRace,
      raceTime: formatStreamDuration(prevRace.startTime), // Update the time for the race
    }));
    setIsLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchRaces(),
        fetchUserBets(),
        fetchBets(),
        fetchRacerOdds(),
      ]);
    };

    fetchData();
    // Set an interval to update the race times every second
    const intervalId = setInterval(updateRaceTimes, 1000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, []);

  const navigateProfile = (racer) => {
    navigate(`${routes.profile}?racerId=${racer.identifier}`);
  };
  const handlePlaceBet = async () => {
    const payload = {
      amount: wager,
      currency: "usdt",
      racerIdentifier: racerId,
      raceIdentifier: id,
    };
    setWager("");
    try {
      const response = await makeApiCall(
        apiRoutes.bets.place,
        "POST",
        payload
        // "show"
      );
      const updatedUser = {
        ...user,
        walletAmount: response.data.walletAmount,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      updateUser(updatedUser);
      fetchUserBets(false);
      // fetchBets();
    } catch (e) {}
  };

  return (
    <>
      {isLoading ? (
        <RacePageSkeleton isMediumScreen={isMediumScreen} />
      ) : (
        <Box
          sx={{
            display: "flex",
            height: "100vh",
          }}
        >
          <LoadingBackdrop isLoading={isLoading} />

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: isMediumScreen ? 0 : "15px",
              pt: isMediumScreen ? 0 : "38px",
              pl: isMediumScreen ? 0 : "38px",
              pb: isMediumScreen ? 18 : 0,
              mb: isMediumScreen ? 0 : 12,
              // mt: 4,
              "&::-webkit-scrollbar": {
                display: "none", // Hide scrollbar in WebKit browsers (Chrome, Safari)
              },
            }}
          >
            {!isMediumScreen && (
              <RaceInfo
                raceData={raceData}
                isMediumScreen={isMediumScreen}
                totalBets={bets.length}
              />
            )}
            <Box mt={isMediumScreen ? 0 : 4}>
              <RaceGame isMediumScreen={isMediumScreen} />

              <Box px={isMediumScreen ? 3 : 0}>
                {isMediumScreen && (
                  <RaceInfo
                    raceData={raceData}
                    isMediumScreen={isMediumScreen}
                    totalBets={bets.length}
                  />
                )}

                <RacerOdds
                  isMediumScreen={isMediumScreen}
                  onClick={navigateProfile}
                  racerOdds={racerOdds}
                />
                <Divider sx={{ background: darkColors.gray, my: 2 }} />

                {/* Betting Pool Card */}
                <Box
                  sx={{
                    background: `linear-gradient(
                      264.63deg,
                      rgba(187, 107, 16, 0.25) 5.84%,
                      rgba(255, 165, 63, 0.25) 20.85%,
                      rgba(100, 98, 98, 0.06) 40.67%
                    )`,
                    borderRadius: 2,
                    p: 2,
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      color="white"
                      textAlign={"left"}
                      fontWeight={600}
                    >
                      Betting Pool
                    </Typography>
                    <Box display="flex" alignItems="center">
                      {/* Coin Icon */}
                      <InlineSvg
                        sx={{ mr: 1, mt: 0.8 }}
                        url={assetIcons.currencyIcon}
                      />
                      <Typography variant="body1" color="gray" fontWeight={700}>
                        {/* Example pool amount, replace with actual data if available */}
                        {bets.reduce((sum, bet) => sum + bet.totalBet, 0)}
                      </Typography>
                      <Typography variant="body2" color="gray" ml={1}>
                        ({bets.length} Bets)
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="white" fontWeight={600}>
                    {raceData?.bettingWindowClosedOn === null &&
                    !isBettingClosed
                      ? "Betting Window Open"
                      : "Betting Window Closed"}
                  </Typography>
                </Box>

                <Divider sx={{ background: darkColors.gray, my: 2 }} />
              </Box>
            </Box>
            <Box mb={isMediumScreen ? 11 : 3}>
              <BetTable rows={bets} />{" "}
            </Box>

            {isMediumScreen && (
              <>
                <Box
                  sx={{
                    position: "fixed",
                    bottom: 90,
                    left: 0,
                    right: 0,
                    transition: "all 0.25s ease-in-out",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <BetPanel
                    isDrawerOpen={isDrawerOpen}
                    toggleDrawer={toggleDrawer}
                  />
                  {raceData.racers && (
                    <SwipeableDrawerComponent
                      isDrawerOpen={isDrawerOpen}
                      setIsDrawerOpen={setIsDrawerOpen}
                      isMediumScreen={isMediumScreen}
                      bets={userBets}
                      racerInfo={raceData?.racers}
                      wager={wager}
                      setWager={setWager} // Pass setter to child
                      racerId={(id) => setRacerId(id)}
                      handlePlaceBet={handlePlaceBet}
                      betOpen={
                        raceData?.bettingWindowClosedOn === null &&
                        !isBettingClosed
                      }
                    />
                  )}
                </Box>
              </>
            )}
          </Box>

          {!isMediumScreen && (
            <Box
              sx={{
                width: { lg: 370, sm: 300 },
                p: 3,
                height: "auto",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Box
                bgcolor={darkColors.userProfileBg}
                mt={3}
                p={3}
                mb={12}
                borderRadius={"16px"}
              >
                <UserDetails />
                <Divider sx={{ background: darkColors.gray }} />
                <Box mt={2}>
                  <BetDetails bets={userBets} />
                </Box>
                {raceData?.bettingWindowClosedOn === null &&
                  !isBettingClosed && (
                    <>
                      <Box mt={4}>
                        {raceData.racers && (
                          <RacerProfile
                            racerInfo={raceData?.racers}
                            racerId={(id) => setRacerId(id)}
                          />
                        )}
                      </Box>
                      <Box mt={4}>
                        <Wager wager={wager} setWager={setWager} />
                      </Box>

                      <Divider sx={{ background: darkColors.gray, my: 2 }} />

                      <Box pb={1}>
                        <GradientButton
                          text={englishTranslations.bet.placeBet}
                          variant="contained"
                          width="100%"
                          disabled={
                            wager > user.walletAmount ||
                            !wager ||
                            isNaN(Number(wager)) ||
                            !isFinite(Number(wager)) ||
                            Number(wager) <= 0
                          }
                          fontSize={fontSizes.heading2}
                          onClick={handlePlaceBet}
                        />{" "}
                      </Box>
                    </>
                  )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
